import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);
  useEffect(() => setOpen(false), [loc]);

  const links = [
    { label: 'Services',     path: '/services' },
    { label: 'Work',         path: '/case-studies' },
    { label: 'About',        path: '/about' },
    { label: 'Contact',      path: '/contact' },
  ];

  return (
    <motion.header
      initial={{ y: -64, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.55, ease: [0.25, 0.1, 0.25, 1] }}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        background: scrolled ? 'rgba(255,255,255,0.92)' : 'transparent',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        borderBottom: scrolled ? '1px solid var(--border)' : '1px solid transparent',
        transition: 'background 0.3s ease, border-color 0.3s ease',
      }}
    >
      <div className="container-site" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 }}>
        {/* Logo */}
        <Link to="/" data-testid="logo-link" style={{ textDecoration: 'none' }}>
          <span className="font-grotesk" style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--ink-950)' }}>
            Outbox
          </span>
          <span className="font-mono" style={{ fontSize: 11, color: 'var(--accent)', marginLeft: 6, letterSpacing: '0.06em' }}>
            Thinking
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex" style={{ alignItems: 'center', gap: 32 }}>
          {links.map(l => (
            <Link
              key={l.path}
              to={l.path}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={`nav-link ${loc.pathname === l.path ? 'active' : ''}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        {/* CTA */}
        <Link to="/contact" data-testid="nav-cta-btn" style={{ textDecoration: 'none' }} className="hidden md:block">
          <span className="btn-primary" style={{ fontSize: 12, padding: '8px 18px', borderRadius: 6 }}>
            Start a Project
          </span>
        </Link>

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          className="md:hidden"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-950)' }}
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            style={{ background: 'var(--canvas)', borderBottom: '1px solid var(--border)', padding: '16px 20px 24px' }}
          >
            {links.map((l, i) => (
              <motion.div key={l.path} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}>
                <Link to={l.path} data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="nav-link" style={{ display: 'block', padding: '12px 0', fontSize: 15, borderBottom: '1px solid var(--border)' }}
                >
                  {l.label}
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
